import { useEffect } from 'react';

import { TopNav } from './sections/TopNav';
import { StatusStrip } from './sections/StatusStrip';
import { Hero } from './sections/Hero';
import { Promise } from './sections/Promise';
import { Pillars } from './sections/Pillars';
import { Preview } from './sections/Preview';
import { Playlists } from './sections/Playlists';
import { Vision } from './sections/Vision';
import { ManifestoCTA } from './sections/ManifestoCTA';
import { Footer } from './sections/Footer';

/**
 * Cursor-reactive halo + subtle parallax on the hero graph.
 * Throttled with requestAnimationFrame so it stays cheap on every move.
 */
function useHeroParallax() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    let raf: number | null = null;
    let mx = 0;
    let my = 0;

    const apply = () => {
      raf = null;
      const hero = document.querySelector<HTMLElement>('.hero');
      if (!hero) return;
      const rect = hero.getBoundingClientRect();
      const xPct = ((mx - rect.left) / rect.width) * 100;
      const yPct = ((my - rect.top) / rect.height) * 100;
      hero.style.setProperty('--mx', `${xPct}%`);
      hero.style.setProperty('--my', `${yPct}%`);

      const stage = hero.querySelector<HTMLElement>('.graph-stage');
      if (stage) {
        const dx = (xPct - 50) * 0.12;
        const dy = (yPct - 50) * 0.12;
        stage.style.setProperty('--parX', `${dx}px`);
        stage.style.setProperty('--parY', `${dy}px`);
      }
    };

    const onMove = (e: PointerEvent) => {
      mx = e.clientX;
      my = e.clientY;
      if (raf == null) raf = requestAnimationFrame(apply);
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      if (raf != null) cancelAnimationFrame(raf);
    };
  }, []);
}

export default function App() {
  useHeroParallax();

  return (
    <>
      <TopNav />
      <StatusStrip />
      <main>
        <Hero />
        <Promise />
        <Pillars />
        <Preview />
        <Playlists />
        <Vision />
        <ManifestoCTA />
      </main>
      <Footer />
    </>
  );
}
